/* ============================================================
   CLX Animations — Claxton Bakery
   - Adds .is-in to sections / cards as they enter the viewport
   - Sets --clx-i index var for staggered reveals
   - Hooks into product variant change for price flash & image cross-fade
   - Re-initialises on Shopify section load (theme editor)
   ============================================================ */
(function () {
  'use strict';

  if (typeof window === 'undefined' || typeof document === 'undefined') return;

  var REDUCED = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (REDUCED) return; // .clx-anim-ready never set in head — content stays visible

  var SUPPORTS_IO = 'IntersectionObserver' in window;

  // Section-level targets — single observer reveals the whole section
  var SECTION_SELECTORS = [
    '.clx-fci',
    '.clx-fi',
    '.clx-vh',
    '.clx-ft',
    '.clx-prod',
    '.clx-col'
  ];

  // Per-item targets — each item reveals individually as it scrolls in
  // (collection cards can be many; we don't want them all to fire from
  // a single section-level trigger)
  var ITEM_SELECTORS = [
    '.clx-col .clx-col__card'
  ];

  function setStaggerIndices(parent, childSelector) {
    var children = parent.querySelectorAll(childSelector);
    for (var i = 0; i < children.length; i++) {
      children[i].style.setProperty('--clx-i', i);
    }
  }

  function revealNow(el) {
    el.classList.add('is-in');
  }

  function observeSections(root) {
    root = root || document;

    // Pre-set stagger indices for known stagger groups
    Array.prototype.forEach.call(root.querySelectorAll('.clx-fi'), function (s) {
      setStaggerIndices(s, '.clx-fi__card');
    });
    Array.prototype.forEach.call(root.querySelectorAll('.clx-col'), function (s) {
      setStaggerIndices(s, '.clx-col__card');
    });

    var sectionEls = [];
    SECTION_SELECTORS.forEach(function (sel) {
      Array.prototype.forEach.call(root.querySelectorAll(sel), function (el) {
        sectionEls.push(el);
      });
    });

    var itemEls = [];
    ITEM_SELECTORS.forEach(function (sel) {
      Array.prototype.forEach.call(root.querySelectorAll(sel), function (el) {
        itemEls.push(el);
      });
    });

    if (!SUPPORTS_IO) {
      // Graceful fallback — just reveal everything immediately
      sectionEls.forEach(revealNow);
      itemEls.forEach(revealNow);
      return;
    }

    // Section observer: triggers when any part of section enters viewport
    var sectionObserver = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          revealNow(entry.target);
          sectionObserver.unobserve(entry.target);
        }
      });
    }, {
      rootMargin: '0px 0px -8% 0px',
      threshold: 0.05
    });

    sectionEls.forEach(function (el) {
      // If element is already above the viewport top on load (instant
      // first paint of hero / first-fold), reveal immediately to avoid
      // any delay or flash.
      var rect = el.getBoundingClientRect();
      if (rect.top < window.innerHeight * 0.9) {
        revealNow(el);
      } else {
        sectionObserver.observe(el);
      }
    });

    // Item observer: card-level stagger as user scrolls collection
    var itemObserver = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          revealNow(entry.target);
          itemObserver.unobserve(entry.target);
        }
      });
    }, {
      rootMargin: '0px 0px -10% 0px',
      threshold: 0.1
    });

    itemEls.forEach(function (el) {
      itemObserver.observe(el);
    });
  }

  /* --------------------------------------------------
     Product variant change: price flash + image fade
     -------------------------------------------------- */
  function hookProductPage() {
    document.addEventListener('change', function (e) {
      var sel = e.target;
      if (!sel || !sel.classList || !sel.classList.contains('clx-prod__variant-select')) return;

      var section = sel.closest('.clx-prod');
      if (!section) return;

      // Defer slightly so the section's own variant handler updates
      // the DOM first.
      setTimeout(function () {
        var price = section.querySelector('.clx-prod__price');
        if (price) {
          price.classList.remove('clx-anim-flash');
          // Force reflow so the animation can replay on subsequent changes
          // eslint-disable-next-line no-unused-expressions
          void price.offsetWidth;
          price.classList.add('clx-anim-flash');
        }
      }, 30);
    });

    // Cross-fade product image when src changes — wrap the section's
    // direct src assignment by intercepting via MutationObserver
    if (!('MutationObserver' in window)) return;

    Array.prototype.forEach.call(document.querySelectorAll('.clx-prod__img'), function (img) {
      var lastSrc = img.getAttribute('src');
      var mo = new MutationObserver(function () {
        var newSrc = img.getAttribute('src');
        if (newSrc && newSrc !== lastSrc) {
          lastSrc = newSrc;
          // Briefly fade out, then fade in once the new image loads
          img.classList.add('clx-anim-img-out');
          var revert = function () {
            img.classList.remove('clx-anim-img-out');
            img.removeEventListener('load', revert);
          };
          if (img.complete && img.naturalWidth > 0) {
            // Image loaded from cache instantly
            setTimeout(revert, 200);
          } else {
            img.addEventListener('load', revert);
            // Safety timeout
            setTimeout(revert, 1200);
          }
        }
      });
      mo.observe(img, { attributes: true, attributeFilter: ['src'] });
    });
  }

  /* --------------------------------------------------
     Init
     -------------------------------------------------- */
  function init() {
    observeSections(document);
    hookProductPage();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Theme editor support — re-observe when a section is reloaded
  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:section:load', function (event) {
      observeSections(event.target);
      // Reveal immediately in editor — no scroll context needed
      var anim = event.target.querySelectorAll(SECTION_SELECTORS.join(',') + ',' + ITEM_SELECTORS.join(','));
      Array.prototype.forEach.call(anim, function (el) {
        el.classList.add('is-in');
      });
    });

    document.addEventListener('shopify:section:select', function (event) {
      var anim = event.target.querySelectorAll(SECTION_SELECTORS.join(',') + ',' + ITEM_SELECTORS.join(','));
      Array.prototype.forEach.call(anim, function (el) {
        el.classList.add('is-in');
      });
    });
  }
})();